// OptionItem.tsx
import React from 'react';
import { Option, QuestionType } from './types';

interface OptionItemProps {
    option: Option;
    questionNumber: number;
    correctAnswers: string[];
    isSelected: boolean; // This prop itself needs to be precise
    showFeedback: boolean;
    onSelect: (optionValue: string | undefined, isChecked: boolean) => void;
    inputType: QuestionType;
}

const OptionItem: React.FC<OptionItemProps> = ({
    option,
    questionNumber,
    correctAnswers,
    isSelected, // This is the crucial prop now
    showFeedback,
    onSelect,
    inputType,
}) => {
    const uniqueId = `question-${questionNumber}-option-${option.option_no}`;

    let itemClassName = 'option-item';
    if (isSelected) {
        itemClassName += ' selected';
    }
    if (showFeedback) {
        // Determine if this option is one of the correct answers (by value or option_no)
        const isCorrectOption =
            (option.value && correctAnswers.map(item => item.toLowerCase()).includes(option.value?.toLowerCase())) ||
            (option.option_no && correctAnswers.includes(option.option_no));

        if (inputType === 'single') {
            if (isSelected) { // If this single option was selected by user
                if (isCorrectOption) { // And it's the correct one
                    itemClassName += ' correct';
                } else { // And it's wrong
                    itemClassName += ' wrong';
                }
            } else if (isCorrectOption) { // If user didn't select this but it's correct
                itemClassName += ' correct-not-selected';
            }
        } else { // Checkbox logic (multiple)
            if (isSelected && isCorrectOption) {
                itemClassName += ' correct'; // User selected this and it's correct
            } else if (isSelected && !isCorrectOption) {
                itemClassName += ' wrong'; // User selected this and it's wrong
            } else if (!isSelected && isCorrectOption) {
                itemClassName += ' correct-not-selected'; // User didn't select this but it's correct
            }
        }
    }

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        // console.log('e.target.checked', option.value, '-->',e.target.checked);
        onSelect(option.value, e.target.checked);
    };

    return (
        <li className={itemClassName}>
            <input
                type={inputType === 'single' ? 'radio' : 'checkbox'}
                id={uniqueId}
                name={`question-${questionNumber}`}
                value={option.value || ''}
                checked={isSelected} // Directly use the isSelected prop
                onChange={handleInputChange}
                disabled={showFeedback}
            />
            <label htmlFor={uniqueId}>
                {option.option_no && <span>{option.option_no}. </span>}
                {option.value || 'N/A'}
            </label>
        </li>
    );
};

export default OptionItem;